// SPDX-FileCopyrightText: Copyright (C) 2026 provide.io llc
// SPDX-License-Identifier: Apache-2.0
import { type ReactElement, useState } from "react";
import { useNavigate } from "react-router";

// "name.*" → wildcard fan-out across the default TLD set.
// Capture is intentionally permissive on the name (any non-empty
// non-dot characters); the FastAPI side enforces label validity.
const WILDCARD_RE = /^([^.\s]+)\.\*$/;

// Resolve a typed query to the right route. The user can express a
// wildcard fan-out by suffixing ``.*`` — handy for "is this name
// taken on any of the common TLDs?".
export function resolveSearchTarget(raw: string): string | null {
  const v = raw.trim().toLowerCase();
  if (!v) return null;
  const wildcardMatch = v.match(WILDCARD_RE);
  if (wildcardMatch) {
    const name = wildcardMatch[1];
    // Default to the 'common' TLD set; the wildcard page also lets
    // users pick a different set after landing.
    return `/wildcard/${name}?set=common`;
  }
  return `/d/${v}`;
}

export function HeaderSearch(): ReactElement {
  const [value, setValue] = useState("");
  const navigate = useNavigate();
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const target = resolveSearchTarget(value);
        if (!target) return;
        navigate(target);
        setValue("");
      }}
    >
      <input
        type="search"
        inputMode="url"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        placeholder="example.com or example.* for a wildcard fan-out"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        aria-label="Search any domain"
        className="h-9 w-full rounded-md border border-border/60 bg-background/60 px-3 font-mono text-[13px] transition-colors placeholder:text-muted-foreground/70 focus-visible:border-accent focus-visible:outline-none"
      />
    </form>
  );
}
